import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2, UserPlus, Link2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { capturaUrl, generarLink, useCreateProspecto } from "./api";

type Props = { open: boolean; onClose: () => void };

export function NuevoProspectoDialog({ open, onClose }: Props) {
  const navigate = useNavigate();
  const create = useCreateProspecto();
  const [nombre, setNombre] = useState("");
  const [telefono, setTelefono] = useState("");
  const [notas, setNotas] = useState("");
  const [busy, setBusy] = useState<null | "manual" | "link">(null);

  // Estado de éxito con link generado
  const [created, setCreated] = useState<{ id: string; folio: string; token: string } | null>(null);
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  function close() {
    setNombre("");
    setTelefono("");
    setNotas("");
    setCreated(null);
    setCopied(false);
    setBusy(null);
    onClose();
  }

  function payload() {
    return {
      nombre_completo: nombre.trim() || undefined,
      telefono: telefono.trim() || undefined,
      notas: notas.trim() || undefined,
    };
  }

  async function crearManual() {
    setBusy("manual");
    try {
      const p = await create.mutateAsync(payload());
      toast.success(`Prospecto ${p.folio} creado`);
      close();
      navigate(`/prospectos/${p.id}`);
    } catch (err) {
      toast.error((err as { message?: string })?.message ?? "No se pudo crear");
    } finally {
      setBusy(null);
    }
  }

  async function crearYLink() {
    setBusy("link");
    try {
      const p = await create.mutateAsync(payload());
      const link = await generarLink(p.id);
      setCreated({ id: p.id, folio: p.folio, token: link.captura_token });
      toast.success(`Prospecto ${p.folio} creado con link`);
    } catch (err) {
      toast.error((err as { message?: string })?.message ?? "No se pudo crear");
    } finally {
      setBusy(null);
    }
  }

  async function copy() {
    if (!created) return;
    await navigator.clipboard.writeText(capturaUrl(created.token));
    setCopied(true);
    toast.success("Link copiado. Pégalo en WhatsApp del prospecto.");
    setTimeout(() => setCopied(false), 1800);
  }

  return (
    // rc137 — móvil: hoja a pantalla completa
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 max-md:items-stretch max-md:p-0" onClick={close}>
      <div
        className="w-full max-w-md rounded-lg border bg-card p-5 shadow-lg max-md:max-w-none max-md:overflow-y-auto max-md:rounded-none max-md:border-0 max-md:pt-[calc(1.25rem+env(safe-area-inset-top))] max-md:pb-[calc(1.25rem+env(safe-area-inset-bottom))] max-md:sheet-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <UserPlus size={18} /> Nuevo prospecto
          </h2>
          <button onClick={close} className="rounded p-1 text-muted-foreground hover:bg-muted">
            <X size={16} />
          </button>
        </div>

        {created ? (
          // ── Éxito: link listo para copiar ──
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
              <Check size={16} /> Prospecto <strong>{created.folio}</strong> creado.
            </div>
            <p className="text-xs text-muted-foreground">
              Copia el link y envíalo por WhatsApp. El prospecto llenará su nombre, teléfono y
              ubicación desde su celular.
            </p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={capturaUrl(created.token)}
                className="flex-1 rounded-md border bg-muted/40 px-3 py-2 font-mono text-xs outline-none"
              />
              <button
                onClick={copy}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
              >
                {copied ? <Check size={15} /> : <Copy size={15} />}
                {copied ? "Copiado" : "Copiar"}
              </button>
            </div>
            <div className="mt-1 flex justify-end gap-2">
              <button onClick={close} className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted">
                Cerrar
              </button>
              <button
                onClick={() => navigate(`/prospectos/${created.id}`)}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
              >
                Ir al prospecto
              </button>
            </div>
          </div>
        ) : (
          // ── Formulario ──
          <>
            <p className="mb-4 text-xs text-muted-foreground">
              ¿No tienes los datos aún? Usa <strong>Crear y generar link</strong> y el prospecto
              llena todo desde su celular. Si ya los tienes, llénalos y usa <strong>Crear</strong>.
            </p>

            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">Nombre completo <span className="text-muted-foreground">(opcional)</span></span>
                <input
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Ej. Juan Pérez García"
                  className="rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">Teléfono <span className="text-muted-foreground">(opcional)</span></span>
                <input
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  placeholder="Ej. 271 123 4567"
                  inputMode="tel"
                  className="rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">Notas <span className="text-muted-foreground">(opcional)</span></span>
                <textarea
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  rows={2}
                  placeholder="Ej. Referido por RES-000123, zona Acatlán"
                  className="resize-none rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
                />
              </label>
            </div>

            <div className="mt-5 flex flex-col gap-2">
              <button
                onClick={crearYLink}
                disabled={busy !== null}
                className="inline-flex items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60"
              >
                {busy === "link" ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />}
                Crear y generar link
              </button>
              <div className="flex justify-end gap-2">
                <button onClick={close} className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted">
                  Cancelar
                </button>
                <button
                  onClick={crearManual}
                  disabled={busy !== null}
                  className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-muted disabled:opacity-60"
                >
                  {busy === "manual" ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />}
                  Crear
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
